import React, { useState } from 'react';
import { Box, Button, FormHelperText, LinearProgress, Link, OutlinedInput, Typography } from '@mui/material';
import { RowAction } from '../../../components/row-action';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ClearIcon from '@mui/icons-material/Clear';
import DocumentAppService from '../../../application/platform/apm/document/document-app-service';
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_SIZE_MB, formatFileSize, validateDocumentFile } from './document-utils';
import { buildImageUrl, imageDisplayName } from './image-utils';

export interface DocumentUploadValue {
    // Caminho no bucket, nome original e tamanho em bytes, gravados juntos no registro
    file?: string;
    originalName?: string;
    size?: number;
}

interface DocumentUploadFieldProps {
    id: string;
    value: DocumentUploadValue;
    onChange: (value: DocumentUploadValue) => void;
    onUploadingChange?: (uploading: boolean) => void;
    error?: boolean;
    helperText?: string;
    clearable?: boolean;
}

// Envia o arquivo para /Document assim que escolhido; o registro só guarda o caminho devolvido
export function DocumentUploadField({ id, value, onChange, onUploadingChange, error, helperText, clearable = true }: DocumentUploadFieldProps) {
    // URL devolvida pelo upload: abre o arquivo na hora, sem depender da URL base
    const [uploaded, setUploaded] = useState<{ fileName?: string; url?: string }>();
    const [uploadError, setUploadError] = useState<string>();
    const [progress, setProgress] = useState<number>();
    const [dragActive, setDragActive] = useState(false);
    const uploading = progress !== undefined;
    // Documentos ficam no mesmo bucket das imagens do portal
    const fileUrl = uploaded?.url && uploaded.fileName === value.file ? uploaded.url : buildImageUrl(value.file);

    async function handleFile(file?: File) {
        if (!file || uploading) return;
        setUploadError(undefined);

        const validationError = validateDocumentFile(file);
        if (validationError) {
            setUploadError(validationError);
            return;
        }

        setProgress(0);
        onUploadingChange?.(true);
        try {
            const result = await DocumentAppService.upload(file, setProgress);
            setUploaded({ fileName: result.fileName, url: result.url });
            onChange({ file: result.fileName, originalName: result.originalFileName || file.name, size: result.size ?? file.size });
        } catch (e: any) {
            setUploadError(e?.message || 'Não foi possível enviar o arquivo.');
        } finally {
            setProgress(undefined);
            onUploadingChange?.(false);
        }
    }

    function handleDrag(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    }

    function handleDrop(e: React.DragEvent) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        handleFile(e.dataTransfer.files?.[0]);
    }

    const displayName = value.originalName || imageDisplayName(value.file);
    const hasError = error || !!uploadError;

    return (
        <Box onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}>
            <input
                style={{ display: 'none' }}
                id={id}
                type="file"
                accept={DOCUMENT_ACCEPT}
                onChange={(e) => {
                    handleFile(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Box
                    sx={{
                        width: 56, height: 56, flexShrink: 0, borderRadius: 1, border: '1px dashed',
                        borderColor: dragActive ? 'primary.main' : 'divider',
                        bgcolor: dragActive ? 'action.hover' : 'background.default',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                >
                    <DescriptionIcon color={value.file ? 'primary' : 'disabled'} />
                </Box>
                <OutlinedInput
                    fullWidth
                    readOnly
                    value={displayName}
                    placeholder={dragActive ? 'Solte o arquivo aqui' : 'Selecione ou arraste um arquivo'}
                    error={hasError}
                    endAdornment={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {clearable && value.file && !uploading && (
                                <RowAction title="Remover arquivo" onClick={() => onChange({})}><ClearIcon fontSize="small" /></RowAction>
                            )}
                            <label htmlFor={id}>
                                <Button variant="contained" component="span" disabled={uploading} sx={{ px: 2 }} endIcon={<CloudUploadIcon />}>
                                    ...
                                </Button>
                            </label>
                        </Box>
                    }
                />
            </Box>
            {uploading && <LinearProgress variant="determinate" value={progress} sx={{ mt: 1 }} />}
            {value.file && fileUrl && !uploading && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                    <Link href={fileUrl} target="_blank" rel="noopener noreferrer">Abrir arquivo</Link>
                    {value.size ? ` · ${formatFileSize(value.size)}` : ''}
                </Typography>
            )}
            {uploadError
                ? <FormHelperText error>{uploadError}</FormHelperText>
                : helperText
                    ? <FormHelperText error={error}>{helperText}</FormHelperText>
                    : <Typography variant="caption" color="text.secondary">PDF, Office/LibreOffice, ZIP/RAR, JPG ou PNG até {DOCUMENT_MAX_SIZE_MB} MB</Typography>}
        </Box>
    );
}
